import {
  FileResult,
  Metadata,
  RowResult,
  UnitsMeasurement,
  UnitsReference,
  UnitsReferenceEnum
} from '@/modules/results/models/interwinnerResult';
import { verifyUnitArrayConsistency, verifyUnitMetadataConsistency } from './verification';
import * as chardet from 'chardet';

export async function extractData(
  files: File[]
): Promise<{ results: Record<string, FileResult>; radionuclides: Set<string>; unitsReference: UnitsReference }> {
  const results: Record<string, FileResult> = {};
  const radionuclides = new Set<string>();
  const unitsReference: UnitsReference = {
    fileName: '',
    metadata: { [UnitsReferenceEnum.MASS]: '', [UnitsReferenceEnum.REALTIME]: '' },
    measurements: { activityUnit: '', uncertaintyValueUnit: '', decisionThresholdUnit: '', detectionLimitUnit: '' }
  };

  for (const file of files) {
    await processFile(file, unitsReference, results, radionuclides);
  }

  return { results, radionuclides, unitsReference };
}

async function processFile(
  file: File,
  unitsReference: UnitsReference,
  results: Record<string, FileResult>,
  radionuclides: Set<string>
): Promise<void> {
  if (unitsReference.fileName.length === 0) {
    unitsReference.fileName = file.name;
  }

  await readFileContent(file).then((fileContent) => {
    const lines = fileContent.split('\n');
    const { metadata, activityArray } = extractMetadataAndActivityArray(unitsReference, lines, file.name);
    const records = convertArrayToStruct(unitsReference, activityArray, file.name);

    for (const entry of Object.entries(records)) {
      const record = entry[1];
      radionuclides.add(record.isotope);
    }

    results[file.name] = {
      metadata: {
        mass: metadata.mass,
        dateOfAcquisition: metadata.dateOfAcquisition,
        realTime: metadata.realTime,
        observations: metadata.observations
      },
      rows: records
    };
  });
}

async function readFileContent(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (e) {
      if (e.target) {
        const arrayBuffer = e.target.result as ArrayBuffer;
        const detectedEncoding = chardet.detect(new Uint8Array(arrayBuffer));

        if (detectedEncoding) {
          const text = new TextDecoder(detectedEncoding).decode(arrayBuffer);
          resolve(text);
        } else {
          reject(new Error('Failed to detect encoding.'));
        }
      }
    };

    reader.onerror = function (error) {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
}

export function extractMetadataAndActivityArray(
  unitsReference: UnitsReference,
  lines: string[],
  fileName: string
): { metadata: Metadata; activityArray: string[] } {
  const metadata: Metadata = { mass: '', dateOfAcquisition: '', realTime: '', observations: [] };
  const activityArray: string[] = [];
  let isActivityArray = false;
  let hasGatheredObs = false;

  for (const [index, line] of lines.entries()) {
    // metadata
    // dateOfAcquisition
    if (line.startsWith("Date d'acquisition")) {
      const rawDateOfAcquisitionField = line.split(': ')[1].trim();
      metadata.dateOfAcquisition = rawDateOfAcquisitionField;
    }
    // realTime
    if (line.startsWith('Temps réel')) {
      let unitRealTime;
      const rawRealTimeField = line.split(': ')[1].trim();
      [metadata.realTime, unitRealTime] = rawRealTimeField.split(' ');
      verifyUnitMetadataConsistency(unitsReference, unitRealTime, UnitsReferenceEnum.REALTIME, fileName);
    }
    // mass
    if (line.startsWith('Quantité échantillon')) {
      let unitMass;
      const rawMassField = line.split(':')[1].trim();
      [metadata.mass, unitMass] = rawMassField.split(' ');
      verifyUnitMetadataConsistency(unitsReference, unitMass, UnitsReferenceEnum.MASS, fileName);
    }
    // observations
    if (line.startsWith('Commentaire') && !hasGatheredObs) {
      hasGatheredObs = true;
      const rawObservation1Field = line.split(':')[1].trim();
      if (rawObservation1Field.length > 0) {
        metadata.observations.push(rawObservation1Field);
        let offset = index + 1;
        let nextLine = lines[offset];
        while (nextLine.startsWith(' ')) {
          const rawObservationNextField = nextLine.trim();
          if (rawObservationNextField.length > 0) {
            metadata.observations.push(rawObservationNextField);
          }
          offset++;
          nextLine = lines[offset];
        }
      }
    }
    // activity array
    if (line.match(/.*Edition des activités et des limites de détection.*/g)) {
      isActivityArray = true;
    } else if (line[0]?.match(/^(?!(\+|\|)).*|\r$/)) {
      isActivityArray = false;
    }
    if (isActivityArray) {
      activityArray.push(line);
    }
  }

  return { metadata, activityArray };
}

export function convertArrayToStruct(
  unitsReference: UnitsReference,
  lines: string[],
  fileName: string
): Record<string, RowResult> {
  const records: Record<string, RowResult> = {};
  let row = 0;
  let unitRow = 0;
  const currentsUnits: UnitsMeasurement = {
    activityUnit: '',
    uncertaintyValueUnit: '',
    decisionThresholdUnit: '',
    detectionLimitUnit: ''
  };

  for (const line of lines) {
    if (line.match(/^\+.*/g)) {
      row++;
    } else if (row === 1) {
      const unitLine = line.replace(/ */g, '').split('|');
      currentsUnits.activityUnit += unitLine[3];
      currentsUnits.uncertaintyValueUnit += unitLine[5];
      currentsUnits.decisionThresholdUnit += unitLine[6];
      currentsUnits.detectionLimitUnit += unitLine[7];
      unitRow++;
      //unit completed
      if (unitRow === 2) {
        verifyUnitArrayConsistency(unitsReference, currentsUnits, fileName);
      }
    } else if (row == 2) {
      const rnArr = line.replace(/ */g, '').split('|');
      const rn: RowResult = {
        isotope: rnArr[2],
        activity: rnArr[3],
        uncertaintyPct: rnArr[4],
        uncertaintyValue: rnArr[5],
        decisionThreshold: rnArr[6],
        detectionLimit: rnArr[7]
      };
      records[rn.isotope] = rn;
    }
  }

  return records;
}
